// store/invoiceSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Invoice, Client, InvoiceUsage, InvoiceStatus, SellerProfile, InvoiceSettings, DashboardStats } from '../types';
import {
  getInvoices,
  getInvoice,
  getClients,
  getInvoiceUsage,
  getDashboardStats as apiGetDashboardStats,
  createInvoice as apiCreateInvoice,
  updateInvoice as apiUpdateInvoice,
  deleteInvoice as apiDeleteInvoice,
  createClient as apiCreateClient,
  updateClient as apiUpdateClient,
  deleteClient as apiDeleteClient,
  toggleClientFavorite as apiToggleClientFavorite,
  markInvoiceSent as apiMarkInvoiceSent,
  markInvoicePaid as apiMarkInvoicePaid,
  markInvoiceReady as apiMarkInvoiceReady,
  markInvoiceConfirmed as apiMarkInvoiceConfirmed
} from '../api/invoices';
import { getSellers, getInvoiceSettings } from '../api/sellers';
import { InvoiceCreateData, InvoiceUpdateData, ClientCreateData } from '../types';

interface InvoiceState {
  invoices: Invoice[];
  clients: Client[];
  sellers: SellerProfile[];
  invoiceSettings: InvoiceSettings | null;
  currentInvoice: Invoice | null;
  usage: InvoiceUsage | null;
  dashboardStats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  statusFilter: InvoiceStatus | null;
  clientFilter: number | null;
}

const initialState: InvoiceState = {
  invoices: [],
  clients: [],
  sellers: [],
  invoiceSettings: null,
  currentInvoice: null,
  usage: null,
  dashboardStats: null,
  loading: false,
  error: null,
  statusFilter: null,
  clientFilter: null
};

// Async thunks for API calls
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchAll',
  async ({ status, clientId }: { status?: InvoiceStatus; clientId?: number } = {}) => {
    const response = await getInvoices(status, undefined, clientId);
    return response;
  }
);

export const fetchInvoiceById = createAsyncThunk(
  'invoices/fetchById',
  async (id: number) => {
    const response = await getInvoice(id);
    return response;
  }
);

export const fetchClients = createAsyncThunk(
  'invoices/fetchClients',
  async ({ activeOnly = true, favoritesFirst = true, search }: { activeOnly?: boolean; favoritesFirst?: boolean; search?: string } = {}) => {
    const response = await getClients(activeOnly, favoritesFirst, search);
    return response;
  }
);

export const fetchSellers = createAsyncThunk(
  'invoices/fetchSellers',
  async (activeOnly: boolean = true) => {
    const response = await getSellers(activeOnly);
    return response;
  }
);

export const fetchInvoiceSettings = createAsyncThunk(
  'invoices/fetchSettings',
  async () => {
    const response = await getInvoiceSettings();
    return response;
  }
);

export const fetchUsage = createAsyncThunk(
  'invoices/fetchUsage',
  async () => {
    const response = await getInvoiceUsage();
    return response;
  }
);

export const createInvoice = createAsyncThunk(
  'invoices/create',
  async (data: InvoiceCreateData, { rejectWithValue }) => {
    try {
      const response = await apiCreateInvoice(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create invoice');
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'invoices/update',
  async ({ id, data }: { id: number; data: InvoiceUpdateData }, { rejectWithValue }) => {
    try {
      const response = await apiUpdateInvoice(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update invoice');
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'invoices/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await apiDeleteInvoice(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete invoice');
    }
  }
);

export const createClient = createAsyncThunk(
  'invoices/createClient',
  async (data: ClientCreateData, { rejectWithValue }) => {
    try {
      const response = await apiCreateClient(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create client');
    }
  }
);

export const updateClient = createAsyncThunk(
  'invoices/updateClient',
  async ({ id, data }: { id: number; data: Partial<ClientCreateData> }, { rejectWithValue }) => {
    try {
      const response = await apiUpdateClient(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update client');
    }
  }
);

export const deleteClient = createAsyncThunk(
  'invoices/deleteClient',
  async (id: number, { rejectWithValue }) => {
    try {
      // Soft delete only - permanent deletion is rejected by the API for
      // clients that already have invoices.
      await apiDeleteClient(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete client');
    }
  }
);

export const toggleClientFavorite = createAsyncThunk(
  'invoices/toggleClientFavorite',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiToggleClientFavorite(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update favorite');
    }
  }
);

export const markInvoiceSent = createAsyncThunk(
  'invoices/markSent',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiMarkInvoiceSent(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark invoice as sent');
    }
  }
);

export const markInvoicePaid = createAsyncThunk(
  'invoices/markPaid',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiMarkInvoicePaid(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark invoice as paid');
    }
  }
);

export const markInvoiceReady = createAsyncThunk(
  'invoices/markReady',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiMarkInvoiceReady(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark invoice as ready');
    }
  }
);

export const markInvoiceConfirmed = createAsyncThunk(
  'invoices/markConfirmed',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await apiMarkInvoiceConfirmed(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark invoice as confirmed');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'invoices/fetchDashboardStats',
  async () => {
    const response = await apiGetDashboardStats();
    return response;
  }
);

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    setCurrentInvoice: (state, action: PayloadAction<Invoice | null>) => {
      state.currentInvoice = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<InvoiceStatus | null>) => {
      state.statusFilter = action.payload;
    },
    setClientFilter: (state, action: PayloadAction<number | null>) => {
      state.clientFilter = action.payload;
    },
    setUsage: (state, action: PayloadAction<InvoiceUsage>) => {
      state.usage = action.payload;
    },
    addInvoice: (state, action: PayloadAction<Invoice>) => {
      state.invoices.unshift(action.payload);
    },
    updateInvoiceInList: (state, action: PayloadAction<Invoice>) => {
      const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
      if (index >= 0) {
        state.invoices[index] = action.payload;
      }
      if (state.currentInvoice?.id === action.payload.id) {
        state.currentInvoice = action.payload;
      }
    },
    removeInvoice: (state, action: PayloadAction<number>) => {
      state.invoices = state.invoices.filter(inv => inv.id !== action.payload);
      if (state.currentInvoice?.id === action.payload) {
        state.currentInvoice = null;
      }
    },
    addClient: (state, action: PayloadAction<Client>) => {
      state.clients.unshift(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchInvoices
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch invoices';
      })
      // Handle fetchInvoiceById
      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInvoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch invoice';
      })
      // Handle fetchClients
      .addCase(fetchClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch clients';
      })
      // Handle fetchUsage
      .addCase(fetchUsage.fulfilled, (state, action) => {
        state.usage = action.payload;
      })
      // Handle createInvoice
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices.unshift(action.payload);
        state.currentInvoice = action.payload;
        // Increment usage counter
        if (state.usage) {
          state.usage.invoices_created += 1;
        }
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle updateInvoice
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
        if (index >= 0) {
          state.invoices[index] = action.payload;
        }
        state.currentInvoice = action.payload;
        // Increment usage counter
        if (state.usage) {
          state.usage.invoices_edited += 1;
        }
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle deleteInvoice
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter(inv => inv.id !== action.payload);
        if (state.currentInvoice?.id === action.payload) {
          state.currentInvoice = null;
        }
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Handle createClient
      .addCase(createClient.fulfilled, (state, action) => {
        state.clients.unshift(action.payload);
      })
      .addCase(createClient.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Handle updateClient / toggleClientFavorite - both return the fresh client
      .addCase(updateClient.fulfilled, (state, action) => {
        const index = state.clients.findIndex(c => c.id === action.payload.id);
        if (index >= 0) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(toggleClientFavorite.fulfilled, (state, action) => {
        const index = state.clients.findIndex(c => c.id === action.payload.id);
        if (index >= 0) {
          state.clients[index] = action.payload;
        }
      })
      .addCase(toggleClientFavorite.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Handle deleteClient - soft delete, so drop it from the active list
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter(c => c.id !== action.payload);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Handle markInvoiceSent
      .addCase(markInvoiceSent.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
        if (index >= 0) {
          state.invoices[index] = action.payload;
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload;
        }
      })
      // Handle markInvoicePaid
      .addCase(markInvoicePaid.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
        if (index >= 0) {
          state.invoices[index] = action.payload;
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload;
        }
      })
      // Handle fetchSellers
      .addCase(fetchSellers.fulfilled, (state, action) => {
        state.sellers = action.payload;
      })
      .addCase(fetchSellers.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch sellers';
      })
      // Handle fetchInvoiceSettings
      .addCase(fetchInvoiceSettings.fulfilled, (state, action) => {
        state.invoiceSettings = action.payload;
      })
      .addCase(fetchInvoiceSettings.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch invoice settings';
      })
      // Handle markInvoiceReady
      .addCase(markInvoiceReady.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
        if (index >= 0) {
          state.invoices[index] = action.payload;
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload;
        }
      })
      // Handle markInvoiceConfirmed
      .addCase(markInvoiceConfirmed.fulfilled, (state, action) => {
        const index = state.invoices.findIndex(inv => inv.id === action.payload.id);
        if (index >= 0) {
          state.invoices[index] = action.payload;
        }
        if (state.currentInvoice?.id === action.payload.id) {
          state.currentInvoice = action.payload;
        }
      })
      // Handle fetchDashboardStats
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch dashboard stats';
      });
  }
});

export const {
  setCurrentInvoice,
  setStatusFilter,
  setClientFilter,
  setUsage,
  addInvoice,
  updateInvoiceInList,
  removeInvoice,
  addClient,
  setLoading,
  setError,
  clearError
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
